'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { skipToken, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useDebounce } from 'use-debounce';
import { isDynamicProbationCountry } from '../lib/dynamic-fields';
import type {
  ApiResponse,
  CountriesWithDynamicProbationMonths,
  CountryField,
  CountryProbationMonthsRequest,
  CountryProbationMonthsResponse,
} from '../types';

interface UseDynamicProbationFieldsParams {
  countryIsoCode?: string;
  countryFields: CountryField[];
  employmentJobId?: string | number | null;
  grossSalary: number;
  contractType?: string | null;
  getProbationMonths: (
    countryIsoCode: CountriesWithDynamicProbationMonths,
    payload: CountryProbationMonthsRequest
  ) => Promise<ApiResponse<CountryProbationMonthsResponse>>;
}

function remapProbationFieldOptions(
  staticOptions: CountryField['options'],
  allowedMonths: number[] | undefined
): CountryField['options'] {
  if (!staticOptions || !allowedMonths?.length) return staticOptions;

  return Object.fromEntries(
    allowedMonths.map((month) => {
      const key = String(month);
      return [key, staticOptions[key] ?? month];
    })
  );
}

export function useDynamicProbationFields({
  countryIsoCode,
  countryFields,
  employmentJobId,
  grossSalary,
  contractType,
  getProbationMonths,
}: UseDynamicProbationFieldsParams) {
  const [debouncedGrossSalary] = useDebounce(grossSalary, 500);
  const probationCountryIsoCode = isDynamicProbationCountry(countryIsoCode)
    ? countryIsoCode
    : undefined;
  const hasProbationField = countryFields.some(
    (field) => field.name === 'probation_period_in_months'
  );
  const hasDynamicProbation = !!probationCountryIsoCode && hasProbationField;

  const { data: probationRange } = useQuery({
    queryKey: [
      'country-probation-months',
      probationCountryIsoCode,
      employmentJobId,
      debouncedGrossSalary,
      contractType,
    ],
    queryFn: probationCountryIsoCode
      ? () =>
          getProbationMonths(probationCountryIsoCode, {
            employment_job_id: Number(employmentJobId),
            gross_salary: debouncedGrossSalary,
            ...(contractType ? { contract_type: contractType } : {}),
          })
      : skipToken,
    enabled: hasDynamicProbation && !!employmentJobId && debouncedGrossSalary > 0,
  });

  return useMemo(() => {
    const probationData = probationRange?.data;
    if (!hasDynamicProbation || !probationData) return countryFields;

    return countryFields.map((field) => {
      const probationField = probationData[field.name as keyof CountryProbationMonthsResponse];
      if (!probationField || !field.options) return field;

      return {
        ...field,
        options: remapProbationFieldOptions(field.options, probationField.options),
      };
    });
  }, [countryFields, probationRange, hasDynamicProbation]);
}
